import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase'; // Ajuste o caminho se necessário
import type { Database, Json } from '../../types/supabase';
type BadgeRow = Database['public']['Tables']['badges']['Row'];
type UserProfile = Database['public']['Tables']['profiles']['Row']; // Adicionar tipo UserProfile
import { getAllBadges, createBadge, updateBadge, deleteBadge, searchUsers } from '../../lib/adminService'; // Adicionar searchUsers
import { awardBadgeToUser, retroactivelyCheckAndAwardBadge } from '../../lib/gamification/badgeService'; // Adicionar awardBadgeToUser e retroactivelyCheckAndAwardBadge
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'react-hot-toast';
import { Edit2, Trash2, PlusCircle, RefreshCw, Search, X, Save, Ban, CheckCircle, Loader2, Award, UserCheck, ShieldAlert } from 'lucide-react'; // Adicionar Award, UserCheck, ShieldAlert
import { useTheme } from '../../contexts/ThemeContext'; // Importar useTheme


// Interface para o formulário de edição/criação
interface BadgeFormState extends Partial<Omit<BadgeRow, 'id' | 'created_at' | 'updated_at'>> {
    id?: string; // Incluir ID para edição
}

const initialFormState: BadgeFormState = {
    badge_key: '',
    name: '',
    description: '',
    icon_url: '',
    criteria_type: 'manual', // 'manual' ou 'automatic'
    criteria_data: null, // JSON object
    xp_reward: 0,
    is_active: true,
    category: 'geral', // Ex: 'geral', 'colecao', 'social', 'participacao', 'especial'
    is_hidden: false,
    start_date: null,
    end_date: null,
};

const jsonExample = `{
  "condition_key": "game_added_count",
  "count_required": 5,
  "filter": { 
    "genre": "Strategy" 
  }
}`;

const BadgesAdminContent: React.FC = () => {
    const { theme } = useTheme(); // Usar o hook useTheme
    const [badges, setBadges] = useState<BadgeRow[]>([]);
    const [filteredBadges, setFilteredBadges] = useState<BadgeRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
    const [editingBadge, setEditingBadge] = useState<BadgeFormState>(initialFormState);
    const [searchTerm, setSearchTerm] = useState('');

    // Estado para atribuição manual
    const [isAwarding, setIsAwarding] = useState(false);
    const [awardLoading, setAwardLoading] = useState(false);
    const [awardUserSearch, setAwardUserSearch] = useState('');
    const [awardUserResults, setAwardUserResults] = useState<UserProfile[]>([]);
    const [selectedAwardUser, setSelectedAwardUser] = useState<UserProfile | null>(null);
    const [selectedAwardBadgeId, setSelectedAwardBadgeId] = useState<string | null>(null);
    const [showAwardConfirm, setShowAwardConfirm] = useState<boolean>(false);

    // Estado para o loading da verificação retroativa
    const [retroactiveCheckLoading, setRetroactiveCheckLoading] = useState<{[badgeId: string]: boolean}>({});

    // Definições de classes para modo escuro
    const mainContainerClasses = `p-4 md:p-6 rounded-lg shadow space-y-6 ${
        theme === 'dark' ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-800'
    }`;
    const sectionTitleClasses = `text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`;
    const formSectionClasses = `mt-6 p-6 border rounded-lg shadow-lg ${
        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-300 bg-white'
    }`;
    const labelClasses = `block text-sm font-medium mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`;
    const mutedTextClasses = `text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`;
    const tableContainerClasses = `overflow-x-auto border rounded-md ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`;
    const tableHeaderClasses = theme === 'dark' ? 'bg-gray-750' : 'bg-gray-50'; // slightly different dark for header
    const tableRowClasses = theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
    const inputGenericClasses = theme === 'dark' ? 'dark' : ''; // Para componentes shadcn que aceitam prop 'dark' ou se adaptam
    const awardSectionClasses = `mt-8 pt-6 border-t space-y-4 p-4 rounded-md ${
        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
    }`;
    const searchResultsContainerClasses = `absolute z-10 mt-1 w-full border rounded-md shadow-lg max-h-60 overflow-y-auto ${
        theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
    }`;
    const searchResultItemClasses = `block w-full text-left px-4 py-2 text-sm ${
        theme === 'dark' ? 'text-gray-200 hover:bg-gray-600' : 'text-gray-700 hover:bg-gray-100'
    }`;
    const selectedUserPillClasses = `p-2 border rounded-md text-sm flex justify-between items-center ${
        theme === 'dark' ? 'bg-blue-900 border-blue-700 text-blue-200' : 'bg-blue-100 border-blue-200 text-blue-800'
    }`;

    const fetchBadges = useCallback(async () => {
        setLoading(true);
        console.log('[BadgesAdminContent] Fetching badges...');
        try {
            const fetchedBadges = await getAllBadges();
            console.log(`[BadgesAdminContent] Fetched ${fetchedBadges.length} badges:`, fetchedBadges);
            setBadges(fetchedBadges);
            setFilteredBadges(fetchedBadges);
        } catch (error) {
            console.error('[BadgesAdminContent] Error in fetchBadges:', error);
            toast.error('Falha ao carregar as badges.');
            setBadges([]);
            setFilteredBadges([]);
        } finally {
            setLoading(false);
            console.log('[BadgesAdminContent] Finished fetching badges.');
        }
    }, []);

    useEffect(() => {
        fetchBadges();
    }, [fetchBadges]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setEditingBadge(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (name: keyof BadgeFormState, checked: boolean) => {
        setEditingBadge(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSelectChange = (name: keyof BadgeFormState, value: string) => {
         setEditingBadge(prev => ({ ...prev, [name]: value }));
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEditingBadge(prev => ({ ...prev, [name]: value === '' ? 0 : Number(value) }));
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
         const { name, value } = e.target;
         setEditingBadge(prev => ({ ...prev, [name]: value || null })); // Define como null se vazio
    };

    // Lida com a complexidade do criteria_data (JSON)
    const handleCriteriaDataChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target;
         // Armazena sempre como string para permitir edição livre, validação ocorre no submit
        setEditingBadge(prev => ({ ...prev, criteria_data: value }));
    };

    const handleCreateOrUpdate = async () => {
        setLoading(true); // Pode usar um loading específico para o form?
        setError(null);

        // Validação básica
        if (!editingBadge.badge_key || !editingBadge.name || !editingBadge.criteria_type) {
             toast.error("Chave da Badge, Nome e Tipo de Critério são obrigatórios.");
             setLoading(false);
             return;
        }

        // Garante que criteria_data é um objeto JSON ou null antes de enviar
        let finalCriteriaData: Json | null = null;
        if (editingBadge.criteria_type === 'automatic' && editingBadge.criteria_data) {
            if (typeof editingBadge.criteria_data === 'string') {
                try {
                    finalCriteriaData = editingBadge.criteria_data.trim() === '' ? null : JSON.parse(editingBadge.criteria_data);
                } catch (e) {
                     toast.error("Formato JSON inválido para Dados do Critério.");
                     setLoading(false);
                     return;
                }
            } else {
                // Se já for objeto (improvável com a lógica atual, mas seguro)
                finalCriteriaData = editingBadge.criteria_data as Json | null;
            }
             if (!finalCriteriaData) {
                 toast.error("Dados do Critério (JSON) são obrigatórios para o tipo Automático.");
                 setLoading(false);
                 return;
             }
        } else {
             // Para manual ou se criteria_data estiver vazio
             finalCriteriaData = null;
        }

        const badgeDataToSave = {
            ...editingBadge,
            criteria_data: finalCriteriaData,
            xp_reward: Number(editingBadge.xp_reward) || 0,
            // Converte datas vazias para null antes de salvar
            start_date: editingBadge.start_date || null,
            end_date: editingBadge.end_date || null,
        };

        try {
            if (editingBadge.id) {
                const updatedBadge = await updateBadge(editingBadge.id, badgeDataToSave);
                if (updatedBadge) {
                    toast.success(`Badge "${updatedBadge.name}" atualizada!`);
                }
            } else {
                const newBadge = await createBadge(badgeDataToSave);
                if (newBadge) {
                    toast.success(`Badge "${newBadge.name}" criada!`);
                }
            }
            setIsFormOpen(false);
            setEditingBadge(initialFormState);
            await fetchBadges();
        } catch (err: any) {
            console.error("Erro ao salvar badge:", err);
            setError(err.message || 'Erro ao salvar badge');
            toast.error(err.message || 'Erro ao salvar badge');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (badge: BadgeRow) => {
        setEditingBadge({
            ...badge,
            // Garante que datas nulas sejam strings vazias para o input date
            start_date: badge.start_date ? badge.start_date.split('T')[0] : '',
            end_date: badge.end_date ? badge.end_date.split('T')[0] : '',
            criteria_data: badge.criteria_data ? JSON.stringify(badge.criteria_data, null, 2) : '' // Formata JSON para textarea
        });
        setIsFormOpen(true);
    };

    const handleDelete = async (id: string) => {
        toast((t) => (
            <div className="flex flex-col items-center">
                <p className="mb-2 text-center font-medium">Remover esta Badge?</p>
                <p className="mb-3 text-center text-sm">Usuários que já a possuem não serão afetados.</p>
                <div className="flex gap-2">
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={async () => {
                            toast.dismiss(t.id);
                            setLoading(true); // Usar loading geral ou específico?
                            const success = await deleteBadge(id);
                            setLoading(false);
                            if (success) {
                                fetchBadges();
                            }
                        }}
                    >
                        <Trash2 className="mr-1 h-4 w-4" /> Confirmar Remoção
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => toast.dismiss(t.id)}>
                        <Ban className="mr-1 h-4 w-4" /> Cancelar
                    </Button>
                </div>
            </div>
        ), { duration: Infinity });
    };

    const openNewForm = () => {
        setEditingBadge(initialFormState);
        setIsFormOpen(true);
    };

    const filterBadges = useCallback(() => {
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        const filtered = badges.filter(badge =>
            badge.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            badge.badge_key.toLowerCase().includes(lowerCaseSearchTerm) ||
            (badge.description && badge.description.toLowerCase().includes(lowerCaseSearchTerm)) || // Check if description exists
            (badge.category && badge.category.toLowerCase().includes(lowerCaseSearchTerm)) // Check if category exists
        );
        setFilteredBadges(filtered);
    }, [searchTerm, badges]);


    useEffect(() => {
        filterBadges();
    }, [searchTerm, badges, filterBadges]);

     // --- Funções de Atribuição Manual ---
     const handleUserSearchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
         const query = e.target.value;
         setAwardUserSearch(query);
         setSelectedAwardUser(null); // Limpa usuário selecionado ao digitar

         if (query.length > 2) {
             setAwardLoading(true);
             try {
                 const results = await searchUsers(query);
                 setAwardUserResults(results);
             } catch (error) {
                 console.error("Erro ao buscar usuários:", error);
                 toast.error("Erro ao buscar usuários.");
                 setAwardUserResults([]);
             } finally {
                 setAwardLoading(false);
             }
         } else {
             setAwardUserResults([]);
         }
     };

     const selectUserForAward = (user: UserProfile) => {
         setSelectedAwardUser(user);
         setAwardUserSearch(user.username || user.email || 'Usuário selecionado'); // Mostra nome/email no input
         setAwardUserResults([]); // Fecha a lista de resultados
     };

     const confirmAwardBadge = () => {
         if (!selectedAwardUser || !selectedAwardBadgeId) {
             toast.error('Selecione um usuário e uma badge para atribuir.');
             return;
         }

         const badgeName = badges.find(b => b.id === selectedAwardBadgeId)?.name || 'esta badge';
         const userName = selectedAwardUser.username || selectedAwardUser.email || 'este usuário';

         if (window.confirm(`Tem certeza que deseja atribuir a badge "${badgeName}" para o usuário "${userName}"?`)) {
             handleAwardBadgeManually(); // Chama a função diretamente se confirmado
         }
     };

     const handleAwardBadgeManually = async () => {
         if (!selectedAwardUser || !selectedAwardBadgeId) {
             // Este check pode ser redundante devido ao check em confirmAwardBadge, mas é seguro manter
             toast.error('Usuário ou Badge não selecionados.');
             return;
         }
         setAwardLoading(true);
         // setShowAwardConfirm(false); // Não necessário mais

         const success = await awardBadgeToUser(selectedAwardUser.id, selectedAwardBadgeId);
         setAwardLoading(false);

         if (success) {
             toast.success(`Badge atribuída com sucesso para ${selectedAwardUser.username}!`);
             // Reset state após sucesso
             setSelectedAwardUser(null);
             setSelectedAwardBadgeId(null);
             setAwardUserSearch('');
             setAwardUserResults([]);
         } else {
             // awardBadgeToUser já mostra toasts específicos para falhas ou se já possui
         }
     };
     // --- Fim Funções de Atribuição Manual ---

    // <<< INÍCIO DA NOVA FUNÇÃO >>>
    const handleRetroactiveCheck = async (badgeId: string, badgeName: string) => {
        if (retroactiveCheckLoading[badgeId]) return; 

        toast((t) => (
            <div className={`flex flex-col items-center p-3 rounded-md ${theme === 'dark' ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-800'} shadow-lg`}>
                <ShieldAlert size={32} className={`mb-2 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'}`} />
                <p className="mb-2 text-center font-semibold">Verificação Retroativa</p>
                <p className="mb-3 text-center text-sm">
                    Verificar "<strong>{badgeName}</strong>" para todos os usuários?
                </p>
                <p className={`mb-3 text-center text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Esta ação pode ser demorada.</p>
                <div className="flex gap-2 w-full">
                    <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        onClick={async () => {
                            toast.dismiss(t.id);
                            setRetroactiveCheckLoading(prev => ({ ...prev, [badgeId]: true }));
                            try {
                                const result = await retroactivelyCheckAndAwardBadge(badgeId);
                                console.log(`[BadgesAdminContent] Resultado da verificação retroativa para "${badgeName}": `, result);
                            } catch (error: any) {
                                console.error(`[BadgesAdminContent] Erro na verificação retroativa para "${badgeName}":`, error);
                                toast.error(`Falha na verificação retroativa para "${badgeName}": ${error.message}`);
                            } finally {
                                setRetroactiveCheckLoading(prev => ({ ...prev, [badgeId]: false }));
                            }
                        }}
                    >
                        {retroactiveCheckLoading[badgeId] ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle className="mr-1 h-4 w-4" />}
                        Confirmar
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => toast.dismiss(t.id)}>
                        <X className="mr-1 h-4 w-4" /> Cancelar
                    </Button>
                </div>
            </div>
        ), { duration: 15000 }); 
    };
    // <<< FIM DA NOVA FUNÇÃO >>>

    return (
        <div className={mainContainerClasses}>
            {/* Seção Gerenciar Badges */}
            <div>
                <h2 className={sectionTitleClasses}>Gerenciar Badges</h2>

                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                    <div className="relative w-full sm:w-72">
                        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                        <Input
                            type="text"
                            placeholder="Buscar por nome, chave, descrição..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                            className={`pl-10 w-full ${inputGenericClasses}`}
                        />
                         {searchTerm && (
                             <Button variant="ghost" size="icon" className={`absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 ${theme === 'dark' ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} onClick={() => setSearchTerm('')}>
                                 <X className="h-4 w-4"/>
                             </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                         <Button variant="outline" onClick={fetchBadges} disabled={loading} className={inputGenericClasses}>
                            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            Recarregar
                        </Button>
                        <Button onClick={openNewForm} disabled={loading} className={inputGenericClasses}>
                            <PlusCircle className="mr-2 h-4 w-4" /> Nova Badge
                        </Button>
                    </div>
                </div>

                {error && <p className={`mb-4 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>{error}</p>}

                {/* Formulário para Criar/Editar Badge (não modal) */}
                {isFormOpen && (
                    <div className={formSectionClasses}>
                        <h3 className={sectionTitleClasses}>{editingBadge.id ? 'Editar Badge' : 'Criar Nova Badge'}</h3>
                        <form onSubmit={(e) => { e.preventDefault(); handleCreateOrUpdate(); }}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="badge_key" className={labelClasses}>Chave da Badge*</label>
                                    <Input id="badge_key" name="badge_key" value={editingBadge.badge_key || ''} onChange={handleInputChange} required placeholder="ex: PIONEER_BADGE" disabled={!!editingBadge.id} className={inputGenericClasses}/>
                                    <p className={mutedTextClasses}>Identificador único (sem espaços/acentos). Não pode ser alterado após criação.</p>
                                </div>
                                <div>
                                    <label htmlFor="name" className={labelClasses}>Nome*</label>
                                    <Input id="name" name="name" value={editingBadge.name || ''} onChange={handleInputChange} required placeholder="Ex: Pioneiro" className={inputGenericClasses} />
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="description" className={labelClasses}>Descrição</label>
                                    <Textarea id="description" name="description" value={editingBadge.description || ''} onChange={handleInputChange} placeholder="O que esta badge representa?" className={inputGenericClasses} />
                                </div>
                                <div>
                                    <label htmlFor="icon_url" className={labelClasses}>URL do Ícone</label>
                                    <Input id="icon_url" name="icon_url" value={editingBadge.icon_url || ''} onChange={handleInputChange} placeholder="https://..." className={inputGenericClasses} />
                                </div>
                                <div>
                                    <label htmlFor="category" className={labelClasses}>Categoria</label>
                                    <Select name="category" value={editingBadge.category || 'geral'} onValueChange={(value: string) => handleSelectChange('category', value)}>
                                        <SelectTrigger className={inputGenericClasses}>
                                            <SelectValue placeholder="Selecione uma categoria" />
                                        </SelectTrigger>
                                        <SelectContent className={inputGenericClasses}>
                                            <SelectItem value="geral">Geral</SelectItem>
                                            <SelectItem value="colecao">Coleção</SelectItem>
                                            <SelectItem value="social">Social</SelectItem>
                                            <SelectItem value="participacao">Participação</SelectItem>
                                            <SelectItem value="mercado">Mercado</SelectItem>
                                            <SelectItem value="especial">Especial</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label htmlFor="criteria_type" className={labelClasses}>Tipo de Critério*</label>
                                    <Select name="criteria_type" value={editingBadge.criteria_type || 'manual'} onValueChange={(value: string) => handleSelectChange('criteria_type', value)} required>
                                        <SelectTrigger className={inputGenericClasses}>
                                            <SelectValue placeholder="Selecione o tipo" />
                                        </SelectTrigger>
                                        <SelectContent className={inputGenericClasses}>
                                            <SelectItem value="manual">Manual</SelectItem>
                                            <SelectItem value="automatic">Automático</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="md:col-span-2">
                                    <label htmlFor="criteria_data" className={labelClasses}>Dados do Critério (JSON)</label>
                                    <Textarea
                                        id="criteria_data"
                                        name="criteria_data"
                                        value={editingBadge.criteria_data as string || ''}
                                        onChange={handleCriteriaDataChange}
                                        placeholder={editingBadge.criteria_type === 'automatic' ? 'Ex: { \"condition_key\": \"post_count\", \"count_required\": 10 }' : 'Não aplicável para Manual'}
                                        rows={4}
                                        className={`font-mono text-sm ${inputGenericClasses}`}
                                        disabled={editingBadge.criteria_type !== 'automatic'}
                                        required={editingBadge.criteria_type === 'automatic'}
                                    />
                                    <p className={mutedTextClasses}>Requerido se o tipo for 'Automático'. Deve ser um JSON válido.</p>
                                    {editingBadge.criteria_type === 'automatic' && (
                                         <details className={`mt-2 text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                             <summary className="cursor-pointer font-medium">Ver exemplo de JSON</summary>
                                             <pre className={`mt-1 p-2 rounded border text-xs font-mono ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-200 text-gray-700'}`}>{jsonExample}</pre>
                                         </details>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="xp_reward" className={labelClasses}>XP Recompensa</label>
                                    <Input id="xp_reward" name="xp_reward" type="number" value={editingBadge.xp_reward ?? 0} onChange={handleNumberChange} min="0" className={inputGenericClasses} />
                                </div>
                                <div>
                                    <label htmlFor="start_date" className={labelClasses}>Data Início (Opcional)</label>
                                    <Input id="start_date" name="start_date" type="date" value={editingBadge.start_date || ''} onChange={handleDateChange} className={inputGenericClasses} />
                                </div>
                                <div>
                                    <label htmlFor="end_date" className={labelClasses}>Data Fim (Opcional)</label>
                                    <Input id="end_date" name="end_date" type="date" value={editingBadge.end_date || ''} onChange={handleDateChange} className={inputGenericClasses} />
                                </div>
                                <div className="flex items-center space-x-2 mt-2">
                                    <Checkbox
                                        id="is_active"
                                        checked={editingBadge.is_active ?? false}
                                        onCheckedChange={(checked: boolean | 'indeterminate') => handleCheckboxChange('is_active', checked === true)}
                                        className={inputGenericClasses}
                                    />
                                    <label htmlFor="is_active" className={`${labelClasses} !mb-0`}>Ativa</label>
                                </div>
                                <div className="flex items-center space-x-2 mt-2">
                                    <Checkbox
                                        id="is_hidden"
                                        checked={editingBadge.is_hidden ?? false}
                                        onCheckedChange={(checked: boolean | 'indeterminate') => handleCheckboxChange('is_hidden', checked === true)}
                                        className={inputGenericClasses}
                                    />
                                    <label htmlFor="is_hidden" className={`${labelClasses} !mb-0`}>Oculta (Admin)</label>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-end gap-3">
                                <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)} className={inputGenericClasses}>Cancelar</Button>
                                <Button type="submit" disabled={loading} className={inputGenericClasses}>
                                    {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                    {editingBadge.id ? 'Salvar Alterações' : 'Criar Badge'}
                                </Button>
                            </div>
                        </form>
                    </div>
                )}

                {!loading && filteredBadges.length === 0 && !isFormOpen && (
                     <p className={`text-center py-4 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Nenhuma badge encontrada{searchTerm ? ' para este termo' : ''}.</p>
                )}
                 {!loading && filteredBadges.length > 0 && (
                    <div className={tableContainerClasses}>
                        <Table>
                            <TableHeader className={tableHeaderClasses}>
                                <TableRow>
                                    <TableHead>Nome / Chave</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Categoria</TableHead>
                                    <TableHead>XP</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className={`text-center py-10 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                            <Loader2 className={`h-8 w-8 animate-spin mx-auto ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                                            <p className="mt-2 text-sm">Carregando badges...</p>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredBadges.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className={`text-center py-10 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                            Nenhuma badge encontrada {searchTerm ? 'para o termo "' + searchTerm + '"' : ''}.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredBadges.map((badge) => (
                                        <TableRow key={badge.id} className={tableRowClasses}>
                                            <TableCell>
                                                <div className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{badge.name}</div>
                                                <div className={mutedTextClasses}>{badge.badge_key}</div>
                                                {badge.is_hidden && <span className={`ml-2 text-xs font-semibold ${theme === 'dark' ? 'text-purple-400' : 'text-purple-600'}`}>(Oculta)</span>}
                                            </TableCell>
                                            <TableCell className={`capitalize ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{badge.criteria_type}</TableCell>
                                            <TableCell className={`capitalize ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{badge.category}</TableCell>
                                            <TableCell className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>{badge.xp_reward || 0}</TableCell>
                                            <TableCell>
                                                {badge.is_active ? <CheckCircle className="text-green-500" size={18} /> : <Ban className="text-red-500" size={18}/>}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex space-x-2 justify-end">
                                                    <Button variant="outline" size="sm" onClick={() => handleEdit(badge)} title="Editar" disabled={isFormOpen} className={inputGenericClasses}>
                                                        <Edit2 size={16} />
                                                    </Button>
                                                    <Button variant="destructive" size="sm" onClick={() => handleDelete(badge.id)} title="Deletar" disabled={isFormOpen} className={inputGenericClasses}>
                                                        <Trash2 size={16} />
                                                    </Button>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        className={`text-xs ${inputGenericClasses}`}
                                                        onClick={() => handleRetroactiveCheck(badge.id, badge.name)}
                                                        disabled={retroactiveCheckLoading[badge.id]}
                                                    >
                                                        {retroactiveCheckLoading[badge.id] ? (
                                                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                        ) : (
                                                            <ShieldAlert className="mr-1 h-3 w-3" />
                                                        )}
                                                        Retroativa
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                 )}
            </div>

            {/* --- Seção Atribuir Badge Manualmente --- */}
            <div className={awardSectionClasses}>
                <h2 className={sectionTitleClasses}>Atribuir Badge Manualmente</h2>
                     <div className="space-y-4">
                         <div className="relative">
                             <label htmlFor="awardUserSearch" className={labelClasses}>Buscar Usuário (Nome ou Email)</label>
                             <div className="relative">
                                 <UserCheck className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                                 <Input
                                     id="awardUserSearch"
                                     type="text"
                                     placeholder="Digite 3+ caracteres para buscar..."
                                     value={awardUserSearch}
                                     onChange={handleUserSearchChange}
                                     className={`pl-10 w-full ${inputGenericClasses}`}
                                     disabled={awardLoading}
                                 />
                                 {awardLoading && (
                                     <Loader2 className={`absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 animate-spin ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`} />
                                 )}
                             </div>
                             {awardUserResults.length > 0 && (
                                 <div className={searchResultsContainerClasses}>
                                     {awardUserResults.map(user => (
                                         <button
                                             key={user.id}
                                             className={searchResultItemClasses}
                                             onClick={() => selectUserForAward(user)}
                                         >
                                             {user.username} ({user.email})
                                         </button>
                                     ))}
                                 </div>
                             )}
                         </div>

                         {selectedAwardUser && (
                             <div className={selectedUserPillClasses}>
                                 <span>Usuário: <strong>{selectedAwardUser.username}</strong> ({selectedAwardUser.email})</span>
                                 <Button variant="ghost" size="icon" className={`h-6 w-6 ${theme === 'dark' ? 'text-blue-300 hover:text-blue-100' : 'text-blue-600 hover:text-blue-800'}`} onClick={() => { setSelectedAwardUser(null); setAwardUserSearch(''); }}>
                                     <X className="h-4 w-4" />
                                 </Button>
                             </div>
                         )}

                         <div>
                             <label htmlFor="awardBadgeSelect" className={labelClasses}>Selecionar Badge</label>
                             <Select
                                 value={selectedAwardBadgeId || ''}
                                 onValueChange={(value) => setSelectedAwardBadgeId(value)}
                                 disabled={!badges.length || awardLoading}
                             >
                                 <SelectTrigger id="awardBadgeSelect" className={inputGenericClasses}>
                                     <SelectValue placeholder={!badges.length ? "Carregando badges..." : "Escolha uma badge para atribuir"} />
                                 </SelectTrigger>
                                 <SelectContent className={inputGenericClasses}>
                                     {badges.map(badge => (
                                         <SelectItem key={badge.id} value={badge.id}>
                                             {badge.name} ({badge.badge_key})
                                         </SelectItem>
                                     ))}
                                 </SelectContent>
                             </Select>
                         </div>

                         <Button
                             onClick={confirmAwardBadge}
                             disabled={!selectedAwardUser || !selectedAwardBadgeId || awardLoading}
                             className={`w-full ${inputGenericClasses}`}
                         >
                             {awardLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Award className="mr-2 h-4 w-4" />}
                             Atribuir Badge Selecionada
                         </Button>
                    </div>
            </div>
        </div>
    );
};

export default BadgesAdminContent; 